import React, { useContext, useMemo } from 'react'

// Libraries
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, ReferenceLine, Label, ReferenceDot, YAxis } from 'recharts'
import Spline from 'cubic-spline'

// Style
import { ChartContainer, XKey, YKey } from './style'
import { ThemeContext } from 'styled-components'

// Types
import { SaltResponseType } from 'types/salt'

type Props = {
  sum: number
  averageData: SaltResponseType
}

/**
 * To solve the bug with recharts mentioned in the comments below, the whole component is memoized with React.memo().
 * This is the props comparator function. It only checks the sum props as the averageData prop is the same throughout
 * the lifecycle of the component.
 */
const areEqual = (prevProps: Props, nextProps: Props) => prevProps.sum === nextProps.sum

/**
 * This component uses recharts to map the salt sum on the salt distribution of edh decks. The distribution data is
 * passed in from the parent as well as the sum.
 *
 * All of the pieces of data are memoized despite being reliant on both of the only two props. This is to resolve an
 * animation issue with the library here: https://github.com/recharts/recharts/issues/846#issuecomment-808350385
 *
 * The graph consists of a bar chart based on the the distribution, a line chart that generates a monotone curve, a
 * reference line for the average salt sum, and a reference dot for the current deck's salt sum.
 *
 * There is no way to calculate a Y value given an X on a generated curve with this library, so in order to properly
 * place the reference dot, we calculate splineAtSum using a lightweight cubic spline library. This very closely matches
 * the curve formula used by recharts.
 */
const SaltDistribution: React.FC<Props> = React.memo(({ sum, averageData }: Props) => {
  const theme = useContext(ThemeContext)

  const { finalDistribution, dx, splineAtSum, maxHeight } = useMemo(() => {
    const spline = new Spline(
      averageData.distribution.map(obj => obj.name),
      averageData.distribution.map(obj => obj.total),
    )
    let splineAtSum = spline.at(sum)
    splineAtSum = splineAtSum < 0 ? 0 : splineAtSum

    const finalDistribution = averageData.distribution.filter(
      entry => entry.name - averageData.step < sum || entry.name <= 100,
    )

    const maxHeight = finalDistribution.reduce((acc, current) => Math.max(current.total, acc), 0)

    let dx = 30
    if (sum < averageData.average) dx = dx - sum * 1.5
    else if (sum < 70) dx = 45
    else dx = -45

    return {
      finalDistribution,
      dx,
      splineAtSum,
      maxHeight,
    }
  }, [averageData, sum])

  return (
    <ChartContainer>
      <h4>Your salt sum compared to other decks</h4>

      <YKey>Number of decks</YKey>

      <ComposedChart
        width={275}
        height={225}
        data={finalDistribution}
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
        <XAxis dataKey="name" type="number" />
        <YAxis hide type="number" domain={[0, maxHeight + 35000]} />
        <CartesianGrid strokeDasharray="3 3" stroke={theme?.lightColor2} />
        <Bar dataKey="total" stackId="a" fill="rgba(50,145,158,0.16)" />
        <ReferenceLine x={averageData.average} stroke={'#4e749c'}>
          <Label
            fill={theme?.lightColor}
            value={`Avg: ${averageData.average}`}
            offset={15}
            dy={30}
            position="right"
            angle={90}
          />
        </ReferenceLine>
        <ReferenceLine x={averageData.median} stroke={'#a05ab6'}>
          <Label
            fill={theme?.lightColor}
            value={`Median: ${averageData.median}`}
            offset={-15}
            dy={5}
            position="right"
            angle={90}
          />
        </ReferenceLine>
        <ReferenceDot isFront stroke={theme?.color} fill={theme?.lightBackground} r={5} x={sum} y={splineAtSum}>
          <Label dx={dx} fill={theme?.color} value={`Yours: ${sum}`} position="top" />
        </ReferenceDot>
        <Line type="natural" dataKey="total" stroke="#ff7300" dot={false} />
      </ComposedChart>

      <XKey>Salt sum</XKey>
    </ChartContainer>
  )
}, areEqual)

export default SaltDistribution
